import { isFunction, isNullish, isObject } from '#library/assertion.library.js';
/**
 * Evaluates candidates in order and returns the first defined result.
 *
 * @param values - Values or zero-argument functions that produce candidate results
 * @returns The first result that is not `undefined`, or `undefined` if no candidate produces one
 */
export function evaluate(...values) {
    for (const val of values) {
        const resolved = isFunction(val) ? val() : val;
        if (resolved !== undefined)
            return resolved; // return as soon as a value is found.
    }
    return undefined;
}
/**
 * Evaluates a value or invokes a function with the provided arguments.
 * If the value is not a function, it is returned as-is.
 *
 * @param val - A scalar value or function to evaluate
 * @param args - Arguments forwarded to the function if val is callable
 * @returns The resolved value
 * @example
 * ```ts
 * evaluateWith(42); // 42
 * evaluateWith((name: string) => `Hello, ${name}`, 'World'); // 'Hello, World'
 * ```
 */
export function evaluateWith(val, ...args) {
    return isFunction(val) ? val(...args) : val;
}
/**
 * Evaluates a value or invokes a function with the provided arguments, stringifying the result.
 *
 * @param val - A value or function to evaluate
 * @param args - Arguments forwarded to the function if val is callable
 * @returns The stringified result
 * @example
 * ```ts
 * evaluateString('test'); // 'test'
 * evaluateString((locale?: string) => locale === 'fr' ? 'Sud' : 'South', 'fr'); // 'Sud'
 * ```
 */
export function evaluateString(val, ...args) {
    const resolved = isFunction(val) ? val(...args) : val;
    return isNullish(resolved) ? '' : String(resolved);
}
/**
 * Evaluates asynchronous candidates in order and stops at the first defined value.
 *
 * @param values - Values, promises, or supplier functions to evaluate.
 * @returns The first defined result, or `undefined` if all candidates resolve to `undefined`.
 */
export async function evaluateAsync(...values) {
    const promises = values.map(val => {
        if (!isFunction(val)) {
            const p = Promise.resolve(val);
            p.catch(() => { });
            return p;
        }
        return undefined;
    });
    for (let i = 0; i < values.length; i++) {
        const val = values[i];
        const resolved = isFunction(val) ? await val() : await promises[i];
        if (resolved !== undefined)
            return resolved;
    }
    return undefined;
}
/**
 * Resolves all top-level properties of a configuration object synchronously.
 * For each property whose value is a function, executes the function and sets the property to its return value.
 *
 * @param config - The configuration object to evaluate
 * @returns A new object with all properties synchronously resolved
 * @example
 * ```ts
 * const evaluated = evaluateConfig({
 *   timeZone: () => 'America/New_York',
 *   locale: 'en-US'
 * });
 * // { timeZone: 'America/New_York', locale: 'en-US' }
 * ```
 */
export function evaluateConfig(config) {
    if (isNullish(config) || !isObject(config))
        return config;
    const result = { ...config };
    for (const key of Object.keys(config)) {
        const val = config[key];
        result[key] = isFunction(val) ? val() : val;
    }
    return result;
}
/**
 * Resolves all top-level properties of a configuration object asynchronously.
 * For each property whose value is a function or Promise, executes/awaits the value and sets the property.
 *
 * @param config - The configuration object to evaluate
 * @returns A Promise resolving to a new object with all properties resolved
 * @example
 * ```ts
 * const evaluated = await evaluateConfigAsync({
 *   key: async () => fetchKey(),
 *   url: 'https://api.openai.com/v1'
 * });
 * // { key: 'sk-...', url: 'https://api.openai.com/v1' }
 * ```
 */
export async function evaluateConfigAsync(config) {
    if (isNullish(config) || !isObject(config))
        return config;
    const result = { ...config };
    const keys = Object.keys(config);
    const values = await Promise.all(keys.map(key => {
        const val = config[key];
        return isFunction(val) ? val() : val;
    }));
    for (let i = 0; i < keys.length; i++)
        result[keys[i]] = values[i];
    return result;
}
